import { TeacherAssignment } from './api/AssignmentTeacherApi';
import { StudentAssignment } from './student/api/AssignmentStudentApi';

export function splitAssignmentsByEndDateCategory<T extends TeacherAssignment | StudentAssignment>(
  assignments: T[],
): {
  assignmentsEndIn24hours: T[];
  assignmentsEndIn7Days: T[];
  assignmentsEndOver7Days: T[];
} {
  const now = new Date();
  const oneDayLater = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  const sevenDaysLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  const assignmentsEndIn24hours: T[] = [];
  const assignmentsEndIn7Days: T[] = [];
  const assignmentsEndOver7Days: T[] = [];

  const sortByDueDateAsc = (a: T, b: T) => new Date(a.to).getTime() - new Date(b.to).getTime();

  assignments.sort(sortByDueDateAsc);

  for (const assignment of assignments) {
    const dueDate = new Date(assignment.to);

    if (dueDate <= oneDayLater) {
      assignmentsEndIn24hours.push(assignment);
    } else if (dueDate <= sevenDaysLater) {
      assignmentsEndIn7Days.push(assignment);
    } else {
      assignmentsEndOver7Days.push(assignment);
    }
  }

  return { assignmentsEndIn24hours, assignmentsEndIn7Days, assignmentsEndOver7Days };
}

function getMonthYear(date: Date, language: string) {
  const dateLanguage = language === 'nl' ? 'nl-BE' : 'fr-BE';

  return date.toLocaleString(dateLanguage, {
    year: 'numeric',
    month: 'long',
  });
}

export function splitAssignmentsByPastEndDateCategory(assignments: TeacherAssignment[], language: string) {
  const now = new Date();

  const day = now.getDay();
  const diffToMonday = (day === 0 ? -6 : 1) - day;
  const startOfThisWeek = new Date(now);
  startOfThisWeek.setDate(now.getDate() + diffToMonday);
  startOfThisWeek.setHours(0, 0, 0, 0);

  const startOfLastWeek = new Date(startOfThisWeek);
  startOfLastWeek.setDate(startOfThisWeek.getDate() - 7);

  const endOfLastWeek = new Date(startOfThisWeek);

  const endedThisWeek: TeacherAssignment[] = [];
  const endedLastWeek: TeacherAssignment[] = [];
  const endedByMonth: Record<string, TeacherAssignment[]> = {};

  const sortByDueDateDesc = (a: TeacherAssignment, b: TeacherAssignment) =>
    new Date(b.to).getTime() - new Date(a.to).getTime();

  assignments.sort(sortByDueDateDesc);

  for (const assignment of assignments) {
    const dueDate = new Date(assignment.to);

    if (dueDate >= startOfThisWeek) {
      endedThisWeek.push(assignment);
    } else if (dueDate >= startOfLastWeek && dueDate <= endOfLastWeek) {
      endedLastWeek.push(assignment);
    } else if (dueDate < startOfLastWeek) {
      const key = getMonthYear(dueDate, language);
      if (!endedByMonth[key]) {
        endedByMonth[key] = [];
      }
      endedByMonth[key].push(assignment);
    }
  }

  return { endedThisWeek, endedLastWeek, endedByMonth };
}
